import { MAX_CHAT_CHARS, type DirectMessageEntry } from "@golazo/shared";
import type { OnlineClient } from "./client.js";

/**
 * Los privados con tus amigos.
 *
 * Vive fuera del lobby, como los amigos, porque un privado tiene que avisar
 * tambien a mitad de partido ("Ana: ¿otra?"). El servidor los guarda (30 dias,
 * los ultimos 200 por pareja), asi que aqui solo se lleva lo que ya se ha
 * cargado y cuantos hay sin leer.
 */
export interface DirectMessages {
  /** La conversacion con un amigo, del mas viejo al mas nuevo. */
  conversation(peer: string): readonly DirectMessageEntry[];
  /** ¿Se ha pedido ya al servidor? Para enseñar "cargando". */
  loaded(peer: string): boolean;
  /** Hay mensajes mas antiguos por cargar. */
  hasMore(peer: string): boolean;
  unread(peer: string): number;
  totalUnread(): number;
  /** Abrir la conversacion: la carga si hace falta y la da por leida. */
  open(peer: string): void;
  /** Ya no se esta mirando ninguna. */
  close(): void;
  /** Con quien se esta hablando ahora, o null. */
  active(): string | null;
  loadOlder(peer: string): void;
  send(peer: string, text: string): void;
  subscribe(fn: () => void): () => void;
  /** Un privado de un amigo que NO se esta mirando: para avisar. */
  onIncoming(fn: (peer: string, entry: DirectMessageEntry) => void): () => void;
  dispose(): void;
}

/** Lo que se guarda en memoria por conversacion. El servidor tiene el resto. */
const KEEP = 200;

export function createDirectMessages(client: OnlineClient): DirectMessages {
  const conversations = new Map<string, DirectMessageEntry[]>();
  const more = new Map<string, boolean>();
  const requested = new Set<string>();
  let unread: Record<string, number> = {};
  let current: string | null = null;
  const listeners = new Set<() => void>();
  const incomingListeners = new Set<(peer: string, entry: DirectMessageEntry) => void>();

  const emit = (): void => {
    for (const fn of listeners) fn();
  };

  const markRead = (peer: string): void => {
    const list = conversations.get(peer);
    const last = list?.at(-1);
    if (last) client.send({ type: "dm:read", with: peer, upTo: last.id });
    if (unread[peer]) {
      unread = { ...unread, [peer]: 0 };
    }
  };

  const merge = (peer: string, entries: readonly DirectMessageEntry[]): void => {
    const list = conversations.get(peer) ?? [];
    const known = new Set(list.map((e) => e.id));
    const fresh = entries.filter((e) => !known.has(e.id));
    if (fresh.length === 0 && conversations.has(peer)) return;
    conversations.set(peer, [...list, ...fresh].sort((a, b) => a.id - b.id).slice(-KEEP));
  };

  const unsub = client.onMessage((msg) => {
    if (msg.type === "welcome") {
      // Otra sesion (reconexion, otra cuenta): lo cargado puede estar viejo.
      conversations.clear();
      more.clear();
      requested.clear();
      if (current) {
        requested.add(current);
        client.send({ type: "dm:history", with: current });
      }
      emit();
      return;
    }
    if (msg.type === "dm:unread") {
      unread = { ...msg.counts };
      if (current) unread[current] = 0;
      emit();
      return;
    }
    if (msg.type === "dm:history") {
      const older = msg.entries;
      merge(msg.with, older);
      more.set(msg.with, msg.more);
      if (msg.with === current) markRead(msg.with);
      emit();
      return;
    }
    if (msg.type === "dm:message") {
      const peer = msg.with;
      // Si la conversacion no esta cargada no se mete a medias: al abrirla
      // llegara entera del servidor, este mensaje incluido.
      if (conversations.has(peer)) merge(peer, [msg.entry]);
      const mine = msg.entry.from === client.clientId;
      if (peer === current) {
        if (!mine) markRead(peer);
      } else if (!mine) {
        unread = { ...unread, [peer]: (unread[peer] ?? 0) + 1 };
        for (const fn of incomingListeners) fn(peer, msg.entry);
      }
      emit();
    }
  });

  return {
    conversation: (peer) => conversations.get(peer) ?? [],
    loaded: (peer) => conversations.has(peer),
    hasMore: (peer) => more.get(peer) ?? false,
    unread: (peer) => unread[peer] ?? 0,
    totalUnread: () => Object.values(unread).reduce((sum, n) => sum + n, 0),
    active: () => current,

    open(peer) {
      current = peer;
      if (!requested.has(peer)) {
        requested.add(peer);
        client.send({ type: "dm:history", with: peer });
      } else {
        markRead(peer);
      }
      unread = { ...unread, [peer]: 0 };
      emit();
    },

    close() {
      current = null;
    },

    loadOlder(peer) {
      const first = conversations.get(peer)?.[0];
      if (!first || !(more.get(peer) ?? false)) return;
      client.send({ type: "dm:history", with: peer, before: first.id });
    },

    send(peer, text) {
      const clean = text.replace(/\s+/g, " ").trim().slice(0, MAX_CHAT_CHARS);
      if (clean) client.send({ type: "dm:send", to: peer, text: clean });
    },

    subscribe(fn) {
      listeners.add(fn);
      return () => listeners.delete(fn);
    },

    onIncoming(fn) {
      incomingListeners.add(fn);
      return () => incomingListeners.delete(fn);
    },

    dispose() {
      unsub();
      listeners.clear();
      incomingListeners.clear();
    },
  };
}
